import { cloneCanvasForExport } from '@/infrastructure/engine2d/export/cloneCanvasForExport';
import { compositeExportCanvas } from '@/infrastructure/engine2d/export/compositeExportCanvas';
import { stripCanvasOverlays } from '@/infrastructure/engine2d/export/stripCanvasOverlays';
import type { Canvas } from 'fabric';
import type { EExportRenderingMode } from '@/application/ports/EExportRenderingMode';

type TGetZoneCanvasParams = {
    backgroundColor?: string;
    fabricCanvas: Canvas;
    imageRenderHeight: number;
    imageRenderWidth: number;
    mode: EExportRenderingMode;
    multiplier?: number;
};

// Snapshot the fabric working zone as an HTMLCanvasElement sized to
// the print target. The mode encodes the intent (BAT / print / 3D
// texture) — stripCanvasOverlays maps that onto the concrete "keep /
// strip which overlay" decisions.
async function getZoneCanvas({
    backgroundColor = 'transparent',
    fabricCanvas,
    imageRenderHeight,
    imageRenderWidth,
    mode,
    multiplier = 1,
}: TGetZoneCanvasParams): Promise<HTMLCanvasElement> {
    const { canvas, workingZoneGroup, workingZoneRect } = await cloneCanvasForExport(fabricCanvas);
    try {
        if (workingZoneRect && !workingZoneRect.width) {
            throw new Error('exportZoneCanvas: working-zone rect has no width');
        }

        const width = Math.floor((workingZoneRect?.width ?? fabricCanvas.width) * multiplier);
        const height = Math.floor((workingZoneRect?.height ?? fabricCanvas.height) * multiplier);
        canvas.set({ width, height, backgroundColor: 'transparent' });

        stripCanvasOverlays(canvas, mode);

        if (workingZoneGroup) {
            canvas.remove(workingZoneGroup);
        }
        canvas.backgroundColor = 'transparent';
        _clearRectFills(canvas);

        return compositeExportCanvas({ backgroundColor, clonedFabricCanvas: canvas, imageRenderHeight, imageRenderWidth });
    } finally {
        // Every run allocates a new fabric.Canvas (with DOM element,
        // event listeners, object graph). Without dispose() it lingers
        // in Fabric's static instance list — subsequent clones then do
        // more bookkeeping and get progressively slower. dispose()
        // detaches all of that.
        await canvas.dispose();
    }
}

// The clone inherits rect fills from the working-zone group; wipe
// them so the rasterised output starts from a transparent surface
// and only fabric-drawn objects contribute pixels.
function _clearRectFills(canvas: Canvas): void {
    canvas.getObjects().forEach((object) => {
        if (object.type === 'Rect') {
            object.fill = 'transparent';
        } else {
            object.set({ objectCaching: false, dirty: true });
        }
    });
    canvas.renderAll();
}

export { getZoneCanvas };
